import React, { memo, useState } from 'react'
import { useSelector } from 'react-redux'

import { RootState } from '../../../admin/redux/store'
import { PageLayoutContainer } from '../../../lib/page-layout-container'
import { LoadBalancerGroupGetAllPagedApiArg, useLoadBalancerGroupDeleteMutation } from '../../redux/api/web-api'
import { notifyError, notifySuccess } from '../../features/notificationsManager/Notify'
import { LoadbalancerTableListWidget } from '../../../support/components/loadbalancer-table-list-widget'
import { useCompanyQuery, useLoadbalancerGroupsQuery } from '../../hooks/useFetchEndpoints'
import useText from '../../hooks/useText'
import { confirm } from '../../uikit/ConfirmDialog'

export const defaultFilter: LoadBalancerGroupGetAllPagedApiArg = {
    id: '',
    name: '',
    companyName: '',
    page: 0,
    noOfItems: 15,
}

export const LoadbalancerPage = memo(function LoadbalancerPage(): React.ReactElement {
    const text = useText()
    const [filter, setFilter] = useState<LoadBalancerGroupGetAllPagedApiArg>(defaultFilter)
    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)
    const partnerId = useSelector((state: RootState) => state.user.partnerId)

    const {
        data: loadBalancerGroups,
        isFetching: groupsLoading,
        refetch: fetchLoadBalancerGroup,
    } = useLoadbalancerGroupsQuery(partnerId!, companyId, filter)

    const { data: companies } = useCompanyQuery(partnerId)

    const filterSubmit = (filter: Partial<LoadBalancerGroupGetAllPagedApiArg>) => {
        setFilter({ ...filter, name: filter?.name?.trim(), id: filter?.id?.trim() })
    }

    const [removeLoadBalancerGroup] = useLoadBalancerGroupDeleteMutation()

    const handleRemoveLBG = async (groupId?: number) => {
        if (!groupId) return

        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: text('lblDeleteLoadBalancerWithId', { lbgId: groupId }),
            variant: 'danger',
        })
        if (!confirmed) return

        try {
            await removeLoadBalancerGroup({ id: groupId }).unwrap()
            notifySuccess(text('lblLoadBalancerDeleteSuccess'))
            fetchLoadBalancerGroup()
        } catch (error) {
            notifyError(text('lblLoadBalancerDeleteError'))
        }
    }

    return (
        <PageLayoutContainer>
            <LoadbalancerTableListWidget
                loadBalancerGroups={loadBalancerGroups}
                companies={companies}
                filter={filter}
                filterSubmit={filterSubmit}
                removeLBGroup={handleRemoveLBG}
                setFilter={setFilter}
                groupsLoading={groupsLoading}
            />
        </PageLayoutContainer>
    )
})
